/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IEducationTrainingData } from './education-training.data';
import { IEducationTrainingProps } from './education-training.props.autogenerated';
import { ICourseEntity, ICourseResponseEntity } from '../../actions/DataServiceEntities.g';
import { QueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { getCourseDataAsync } from '../../actions/DataActionExtension.g';
import Pagination from '../../components/Pagination';
import { CourseTableComponent, ICourseTableComponentResources } from './components/CourseTable.component';
import { reaction } from 'mobx';
import { ICartState } from '@msdyn365-commerce/global-state';
import { observer } from 'mobx-react';

/**
 * Props for the view component
 */
export interface IEducationTrainingViewProps extends IEducationTrainingProps<IEducationTrainingData> {
    modelTable: React.ReactNode;
    pagination: React.ReactNode;
}

/**
 *  View state for view component
 */
export interface IEducationTrainingViewState {
    isLoading: boolean;
}

/**
 * Component state
 */
interface IEducationTrainingState {
    page: number;
    courses: ICourseEntity[];
    totalCount: number;
    cart: ICartState | undefined;
    viewState: IEducationTrainingViewState;
}

/**
 *
 * EducationTraining component
 * @extends {React.PureComponent<IEducationTrainingProps<IEducationTrainingData>>}
 */
@observer
class EducationTraining extends React.PureComponent<IEducationTrainingProps<IEducationTrainingData>, IEducationTrainingState> {
    constructor(props: IEducationTrainingProps<IEducationTrainingData>) {
        super(props);

        const queryResultSettings = QueryResultSettingsProxy.fromInputData(props.context.actionContext).QueryResultSettings;
        let page = 1;
        if (queryResultSettings.Paging?.Skip && queryResultSettings.Paging.Skip > 0) {
            page = (this.props.config.itemsPerPage || 9) / queryResultSettings.Paging.Skip;
        }
        const viewState = {
            isLoading: false
        };
        this.state = {
            page,
            courses: this.props.data.courseResults.result?.courseResponse.Courses || [],
            totalCount: this.props.data.courseResults.result?.courseResponse.TotalCount || 0,
            cart: this.props.data.cart.result,
            viewState
        };

        this.handlePaginationPageChange = this.handlePaginationPageChange.bind(this);
        this.setCourseResults = this.setCourseResults.bind(this);
        this.setCartState = this.setCartState.bind(this);
        this.setLoading = this.setLoading.bind(this);
    }

    public componentDidMount(): void {
        reaction(
            () => this.props.data.courseResults.result?.courseResponse,
            () => this.setCourseResults(this.props.data.courseResults.result!.courseResponse)
        );
        reaction(
            () => this.props.data.cart.result?.cart,
            () => this.setCartState(this.props.data.cart.result!)
        );
    }

    private setCourseResults(courseResponse: ICourseResponseEntity) {
        this.setState({
            courses: courseResponse.Courses || [],
            totalCount: courseResponse.TotalCount
        });
    }

    private setCartState(cart: ICartState) {
        this.setState({
            cart: cart
        });
    }

    public setLoading(setLoading: boolean) {
        this.setState(prevState => ({ viewState: { ...prevState.viewState, isLoading: setLoading } }));
    }

    /**
     * Updates the results of the model when paginating
     * @private
     */
    private updateModelResults() {
        const top = this.props.config.itemsPerPage || 9;
        const queryResultSettings: QueryResultSettings = {
            Paging: {
                Top: top,
                Skip: this.state.page * top - top
            }
        };
        const {
            context: { actionContext }
        } = this.props;

        void getCourseDataAsync({ callerContext: actionContext, queryResultSettings: queryResultSettings }).then(result => {
            this.setState({
                courses: result[0].Courses || [],
                totalCount: result[0].TotalCount
            });
        });
    }

    /**
     * Renders the model table
     * @param modelsList model list for the table
     * @param totalCount total count of models for pagination
     * @private
     */
    private getModelTable(
        courseList: ICourseEntity[],
        additionalInfo: Msdyn365.RichText | undefined,
        totalCount: number,
        cart: ICartState | undefined,
        viewState: IEducationTrainingViewState,
        setLoading: (setLoading: boolean) => void
    ) {
        if (!courseList?.length) {
            return null;
        }

        const {
            courseListHeading,
            courseListResultsHeading,
            courseNameHeading,
            courseDateHeading,
            courseRegistrationDetailsHeading,
            courseAddToCartHeading,
            addToCartButtonLabel,
            startLabel,
            endLabel,
            registrationEndsLabel,
            maximumAttendeesLabel,
            currentAttendeesLabel,
            courseExpiredMessage,
            courseFullMessage
        } = this.props.resources;
        const educationTrainingResources: ICourseTableComponentResources = {
            courseListHeading,
            courseListResultsHeading,
            courseNameHeading,
            courseDateHeading,
            courseRegistrationDetailsHeading,
            courseAddToCartHeading,
            addToCartButtonLabel,
            startLabel,
            endLabel,
            registrationEndsLabel,
            maximumAttendeesLabel,
            currentAttendeesLabel,
            courseExpiredMessage,
            courseFullMessage
        };
        return (
            <CourseTableComponent
                courseList={courseList}
                resources={educationTrainingResources}
                additionalInfo={additionalInfo}
                totalCount={totalCount}
                channelId={this.props.context.request.apiSettings.channelId}
                cart={cart}
                ctx={this.props.context}
                viewState={viewState}
                setLoading={setLoading}
            />
        );
    }

    /**
     * Handles user pagination
     * @param pageNumber page to change to
     * @private
     */
    private handlePaginationPageChange(pageNumber: number) {
        this.setState(
            {
                page: pageNumber
            },
            () => this.updateModelResults()
        );
    }

    /**
     * renders the pagination
     * @private
     */
    private renderPagination() {
        const {
            config: { itemsPerPage },
            resources: { previousText, nextText, paginationAriaLabel }
        } = this.props;
        if (this.state.totalCount === 0) {
            return '';
        }
        return (
            <Pagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={paginationAriaLabel}
                qsp='skip'
                items={this.state.totalCount}
                itemsPerPage={itemsPerPage || 9}
                url='localhost:4000'
                startingItem={1}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                onPageChange={this.handlePaginationPageChange}
            />
        );
    }

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev' onClick={() => this.handlePaginationPageChange(this.state.page - 1)}>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next' onClick={() => this.handlePaginationPageChange(this.state.page + 1)}>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    public render(): JSX.Element | null {
        const { courses, totalCount, cart } = this.state;

        const viewProps: IEducationTrainingViewProps = {
            ...this.props,
            modelTable: this.getModelTable(
                courses,
                this.props.config.additionalInfo,
                totalCount,
                cart,
                this.state.viewState,
                this.setLoading
            ),
            pagination: this.renderPagination()
        };
        return this.props.renderView(viewProps);
    }
}

export default EducationTraining;
